import Grid from "@mui/material/Grid2";
import { Box, List, ListItem, ListItemText, ListSubheader, Typography } from "@mui/material";
import { intl } from "LocaleWrapper";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { TierUnit, TierPriceUnit } from "__generated__/graphql";
import type { TierForm as TierFormType } from "types/index";
import { Button, FormGroup } from "@cofano/cck";
import NumberInput from "components/cck/NumberInput";
import FormField from "components/cck/FormField";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { TableRowsOutlined } from "@mui/icons-material";
import TierPriceUnitSelect from "view/calculationmethods/components/select/TierPriceUnitSelect";
import DataFormat from "components/utils/DataFormat";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";


type FormValues = {
	agreements: {
		calculationMethod: {
			tierUnit?: TierUnit;
			tiers?: TierFormType[];
		}
	}[];
}

type TierFormProps = {
	index: number;
	currency: CurrencyFragment;
}


const PriceUnitByTier = {
	[TierUnit.CargoQuantity]: [ TierPriceUnit.Lumpsum, TierPriceUnit.PerUnit ],
	[TierUnit.GasoilPrice]: [ TierPriceUnit.Lumpsum, TierPriceUnit.SurchargePercentage ],
	[TierUnit.WaterLevel]: [ TierPriceUnit.MinimumQuantityPayable, TierPriceUnit.VesselDraftInMeters, TierPriceUnit.SurchargePercentage ],
};

type TierRowProps = {
	index: number;
	tierIndex: number;
	tierUnit: TierUnit;
	currency: CurrencyFragment;
}

const TierRow = ({ index, tierIndex, tierUnit, currency }: TierRowProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();
	const priceUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.tiers.${tierIndex}.priceUnit` });

	const getPriceLabel = () => {
		if (!priceUnit) {
			return "";
		}
		const labelKey = `TIER_PRICE_LABEL_${priceUnit}`;
		return intl.formatMessage({ id: labelKey, defaultMessage: intl.formatMessage({ id: `TIER_PRICE_UNIT_${priceUnit}` }) });
	};

	return (
		<>
			<Grid size={1.5}>
				<FormField errors={errors} name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.minimum`}>
					<FormGroup label={intl.formatMessage({ id: `TIER_MINIMUM_${tierUnit}` })} overflow="hidden" required>
						<Controller
							name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.minimum`}
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<NumberInput
									{...field}
									addonBefore={tierUnit === TierUnit.GasoilPrice ? currency.symbol : ""}
									addonAfter={tierUnit === TierUnit.CargoQuantity ? DataFormat.UNITS.MTON : tierUnit === TierUnit.WaterLevel ? "m" : ""} />
							)} />
					</FormGroup>
				</FormField>
			</Grid>

			<Grid size={1.5}>
				<FormField errors={errors} name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.maximum`}>
					<FormGroup label={intl.formatMessage({ id: `TIER_MAXIMUM_${tierUnit}` })} overflow="hidden" required>
						<Controller
							name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.maximum`}
							rules={{ required: true }}
							control={control}
							render={({ field }) => (
								<NumberInput
									{...field}
									addonBefore={tierUnit === TierUnit.GasoilPrice ? currency.symbol : ""}
									addonAfter={tierUnit === TierUnit.CargoQuantity ? DataFormat.UNITS.MTON : tierUnit === TierUnit.WaterLevel ? "m" : ""} />
							)} />
					</FormGroup>
				</FormField>
			</Grid>

			<Grid size={2.5}>
				<FormField errors={errors} name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.priceUnit`}>
					<FormGroup label={intl.formatMessage({ id: "TIER_PRICE_UNIT" })} overflow="hidden" required>
						<Controller
							name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.priceUnit`}
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<TierPriceUnitSelect options={PriceUnitByTier[tierUnit]} {...field} />
							)} />
					</FormGroup>
				</FormField>
			</Grid>

			{priceUnit && (
				<Grid size={2.5}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.price`}>
						<FormGroup label={getPriceLabel()} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.price`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonAfter={priceUnit === TierPriceUnit.VesselDraftInMeters ? "m" : priceUnit === TierPriceUnit.SurchargePercentage ? "%" : (priceUnit === TierPriceUnit.PerUnit && tierUnit === TierUnit.CargoQuantity) ? `${intl.formatMessage({ id: "UNIT_PER" })} ${DataFormat.UNITS.MTON}` : ""}
										addonBefore={(priceUnit === TierPriceUnit.Lumpsum || priceUnit === TierPriceUnit.PerUnit) ? currency.symbol : ""}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			)}

			{tierUnit === TierUnit.CargoQuantity && priceUnit === TierPriceUnit.Lumpsum && (
				<Grid size={2.5}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.pricePerMtAdditionalToLumpsum`}>
						<FormGroup
							tooltip={intl.formatMessage({ id: "TIER_PRICE_PER_MT_ADDITIONAL_INFO" })}
							label={intl.formatMessage({ id: "TIER_PRICE_PER_MT" })}
							overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.tiers.${tierIndex}.pricePerMtAdditionalToLumpsum`}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			)}
		</>
	);
};

const TierForm = ({ index, currency }: TierFormProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();

	const tierUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.tierUnit` });

	const { fields, append, remove } = useFieldArray({
		control: control,
		keyName: "generatedId",
		name: `agreements.${index}.calculationMethod.tiers`,
	});

	const defaultTier = {
		id: undefined,
		priceUnit: undefined,
		price: 0,
		minimum: 1,
		maximum: 10,
	};

	if (!tierUnit) {
		return null;
	}

	return (
		<Grid container size={12}>
			<Grid size={12} marginTop={2}>
				<List
					dense
					subheader={
						<ListSubheader
							component="div"
							sx={{ padding: 0 }}
							disableSticky>
							<Box sx={{  display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<Box sx={{  display: "flex", alignItems: "center" }}>
									<TableRowsOutlined fontSize="small" />
									<Typography color={`${errors.agreements?.[index]?.calculationMethod?.tiers ? "red" : "textPrimary"}`}
										fontSize={13}
										fontWeight="medium"
										marginLeft={1}>
										{intl.formatMessage({ id: "TIERS" })} <span style={{ color: "red" }}>*</span>
									</Typography>
								</Box>
								<Button
									onClick={() => append({ ...defaultTier })}
									icon={<AddIcon />}
									size="small"
									variant="outlined" />
							</Box>
						</ListSubheader>
					}
				>
					{fields.map((tier, tierIndex) => (
						<ListItem
							key={tier.id || tier.generatedId}
							disablePadding
							secondaryAction={
								<Button
									onClick={() => remove(tierIndex)}
									icon={<DeleteIcon />}
									size="small"
									variant="text" />
							}>
							<Grid container
								spacing={2}
								size={12}
								marginTop={2}>
								<Grid size={1}>
									<ListItemText
										sx={{ margin: 0 }}
										primary={
											<Typography component="span"
												fontSize={13}
												fontStyle="normal"
												fontWeight="medium">
												{intl.formatMessage({ id: "TIER" })} {tierIndex + 1}
											</Typography>
										} />
								</Grid>

								<TierRow
									index={index}
									tierIndex={tierIndex}
									tierUnit={tierUnit}
									currency={currency} />
							</Grid>
						</ListItem>
					))}
				</List>
			</Grid>
		</Grid>
	);
};

export default TierForm;
